import _ from "lodash";
import { DateTime } from "luxon";
import React from "react";
import { Link } from "rhoto-router";
import { useAPIData } from "../api";
import { getDimension } from "../data/dimensions";
import useVisibility from "../hooks/useVisibility";
import EffortDensity from "./EffortDensity";
import DataContext from "./chart/DataContext";
import Map from "./chart/Map";
import { Summary } from "../activity";
import { findNewRecords, BestEffortsList } from "./BestEfforts";

const FORMAT = {
  month: "long" as const,
  day: "numeric" as const,
  weekday: "short" as const,
  hour: "numeric" as const,
  minute: "2-digit" as const,
};

const Stat = ({
  label,
  value,
  children,
}: {
  label: string;
  value?: string;
  children?: React.ReactNode;
}) => (
  <div className="col-6 col-lg-3">
    <dt>{_.startCase(label)}</dt>
    <dd>
      {value}
      {children}
    </dd>
  </div>
);

function PlaceholderActivityEntry() {
  const stats = ["distanceMi", "timeSeconds", "paceMinMi", "elevation"];

  return (
    <div className="card-body">
      <div className="container">
        <div className="row">
          <div className="col-md-7">
            <h5 className="card-title my-3 placeholder">Placeholder</h5>
            <div className="container">
              <p className="placeholder">placeholder</p>

              <div className="effort-density" />
              <hr />

              <div className="row">
                {stats.map((dimensionName) => {
                  const dimension = getDimension(dimensionName);
                  return (
                    <Stat key={dimensionName} label={dimension.displayLabel}>
                      <span className="placeholder">placeholder</span>
                    </Stat>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="col-md-5">
            <div className="map placeholder" />
          </div>
        </div>
      </div>
    </div>
  );
}

interface CompressedPoint {
  wmpX: number;
  wmpY: number;
  smoothZScore: number;
}

// An activity is published to the feed as soon as its summary is fetched,
// which is well before it reaches the front of its user's queue. Until it
// is scored it has no `analysis` at all — distinct from a scored run with
// no GPS, which has an `analysis` without `compressed`. Without the
// distinction both render as 🗺️🚫 and a queued run reads as broken.
export function isAwaitingProcessing(entry: Summary) {
  return !entry.analysis;
}

function QueuedNotice() {
  return (
    <p className="text-muted fst-italic mb-0">
      <span
        className="spinner-border spinner-border-sm me-2"
        role="status"
        aria-hidden="true"
      />
      Queued for analysis — effort and map appear once it&rsquo;s processed.
    </p>
  );
}

export function ActivityListEntryMap({
  entry,
  children,
}: {
  entry: Summary;
  children?: React.ReactNode;
}) {
  if (isAwaitingProcessing(entry)) {
    return (
      <span
        className="treadmill-icon"
        role="img"
        aria-label="hourglass emoji, queued for analysis"
        title="Queued for analysis"
      >
        ⏳
      </span>
    );
  }

  if (entry.analysis && entry.analysis.compressed) {
    const data: CompressedPoint[] = entry.analysis.compressed.map(
      ([wmpX, wmpY, smoothZScore]) => ({
        wmpX,
        wmpY,
        smoothZScore,
      }),
    );

    return (
      <DataContext.Provider value={data}>
        <Map<CompressedPoint>
          strokeWidth={3}
          tiles="nationalMap"
          wmpX="wmpX"
          wmpY="wmpY"
          color="smoothZScore"
          outline
          legend={false}
        >
          {children}
        </Map>
      </DataContext.Provider>
    );
  } else {
    return (
      <span
        className="treadmill-icon"
        role="img"
        aria-label="no map emoji, treadmill emoji"
      >
        🗺️🚫
      </span>
    );
  }
}

function VisibleActivityEntry({
  id,
  userId,
}: {
  id: string | number;
  userId: string | number;
}) {
  const record = useAPIData<Summary>(`/activities/${userId}/${id}`);

  const newRecords = React.useMemo(
    () => findNewRecords(record?.analysis?.bestEfforts),
    [record?.analysis?.bestEfforts],
  );

  if (record) {
    const start = DateTime.fromMillis(record.startTime);
    const stats = {
      distanceMi: record.distanceMi,
      timeSeconds: record.movingTimeSeconds,
      paceMinMi: record.paceMinMi,
      elevation: record.elevationGainFt,
      watts: record.meanWatts,
    };

    const startString = start.toLocaleString({
      ...FORMAT,
      year: start.year === new Date().getFullYear() ? undefined : "numeric",
    });

    // A queued activity has no per-point data yet, and the analysis view
    // renders an empty shell when its data key is missing — so the card
    // shows what we know but does not offer a way through to a blank page.
    const queued = isAwaitingProcessing(record);

    const body = (onClick?: React.MouseEventHandler) => (
      <div className="card-body" onClick={onClick}>
        <div className="container">
          <div className="row">
            <div className="col-md-7">
              <h5 className="card-title my-3">
                {queued ? (
                  <>
                    {record.race ? "🏅" : null} {record.name}
                  </>
                ) : (
                  <Link href={`/activity/${userId}/${id}`}>
                    {record.race ? "🏅" : null} {record.name}
                  </Link>
                )}
              </h5>
              <div className="container">
                {start ? <p>{startString}</p> : null}
                {queued ? <QueuedNotice /> : null}
                {record.analysis &&
                record.analysis.effortDensity &&
                record.analysis.meanEffort ? (
                  <div className="effort-density">
                    <EffortDensity
                      dimension="smoothZScore"
                      meanEffort={record.analysis.meanEffort}
                      effortDensity={record.analysis.effortDensity}
                    />
                  </div>
                ) : null}
                {record.analysis &&
                record.analysis.durationEffortDensity &&
                record.analysis.durationEffortDensity.bins.some(
                  (x) => x !== 0,
                ) ? (
                  <div className="effort-density">
                    <EffortDensity
                      dimension="durationEffortPercentile"
                      meanEffort={record.analysis.meanDurationEffort}
                      effortDensity={record.analysis.durationEffortDensity}
                    />
                  </div>
                ) : null}
                <hr />

                <div className="row">
                  {Object.entries(stats).map(([dimensionName, value]) => {
                    const dimension = getDimension(dimensionName);
                    if (value) {
                      return (
                        <Stat
                          key={dimensionName}
                          label={dimension.displayLabel}
                          value={dimension.labelValue(value)}
                        />
                      );
                    } else {
                      return null;
                    }
                  })}
                </div>
                <div className="row">
                  <BestEffortsList newRecords={newRecords} />
                </div>
              </div>
            </div>
            <div className="col-md-5">
              <div className="map">
                <ActivityListEntryMap entry={record} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    if (queued) return body();

    return (
      <Link href={`/activity/${userId}/${id}`}>
        {({ onClick }: { onClick: React.MouseEventHandler }) => body(onClick)}
      </Link>
    );
  } else return <PlaceholderActivityEntry />;
}

export default function ActivityEntry({
  id,
  userId,
}: {
  id: string | number;
  userId: string | number;
}) {
  const [ref, visible] = useVisibility<HTMLDivElement>({ threshold: 0 });

  return (
    <div className="card activity-list-entry my-3 mx-1" key={id} ref={ref}>
      {visible ? (
        <VisibleActivityEntry id={id} userId={userId} />
      ) : (
        <PlaceholderActivityEntry />
      )}
    </div>
  );
}
